import React, { useState, useRef, useEffect, CSSProperties } from 'react';
import { ChevronDown, ChevronUp, Power, CheckCircle, Clock, Plus, Settings, ArrowRight, X, Zap, AlertTriangle } from 'lucide-react';
import hs from '../header-status/header-status.module.scss';
import { botSelectByQuery, DBBotThunks, DBBotDeploymentThunks } from '@common-reducers/DBServiceThunks';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { Bot as BotType } from '@common/models/bot';
import { toggleBotActivationThunk, createAndReRouteToNewBotEditorThunk } from '@common/reducers/ChatbotThunks';
import { setIsReadyToLoadFlow } from '@common/reducers/ChatbotReducer';
import { useRouter } from '@common-reducers/hooks/use-router';
import { selectIsDarkMode } from '@common-reducers/UserSelectors';
// Deep-import the small credit widgets from their specific source files rather
// than the credits-usage barrel (index.ts). The barrel re-exports credits-usage.tsx,
// which statically imports recharts + d3 + victory-vendor (~360+981 module refs).
// With tree-shaking off in the extension build (mode: development), any barrel
// import drags that whole chart cluster into the content bundle (app.js) even
// though the chart itself is never rendered in the content script — it only
// lives on the website route (CreditsUsageView). Deep-importing keeps recharts
// out of app.js. See PERF_AUDIT_PROGRESS.md Round 12.
import { CreditStatusBadge } from '@common/components/credits-usage/credit-status-badge';
import { CreditWarningAlert } from '@common/components/credits-usage/credit-warning-alert';
import { useCreditManagement } from '@common/hooks/useCreditManagement';
import { openCreditsPage } from '@common/services/utils';
import { btLayoutSidebarActions } from '@common-reducers/BtLayoutSidebarReducer';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common-config/constants';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { usePortalContainer } from '@/common/hooks/use-portal-container';

// Define styles similar to connected-engines component
const styles: Record<string, CSSProperties> = {
  container: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  // The header chip (button, dot, label) now comes from
  // header-status.module.scss, shared with the plan / keep-awake / engine chips.
  expandedView: {
    position: 'absolute',
    top: '100%',
    right: '0',
    marginTop: '8px',
    backgroundColor: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    borderColor: 'var(--color-border)',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    padding: '16px',  
    minWidth: '380px',
    zIndex: 1000,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  title: {
    fontWeight: 500,
    margin: 0,
  },
  closeButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px',
    color: '#6b7280',
  },
  // Additional styles for the content
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px 0',
  },
  loadingSpinner: {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    border: '2px solid var(--color-border)',
    borderTopColor: '#22c55e',
    animation: 'spin 1s linear infinite',
    marginBottom: '8px',
  },
  loadingText: {
    fontSize: '14px',
    color: 'var(--color-text-muted, #6b7280)',
  },
  createButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    backgroundColor: '#22c55e',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
  },
  botItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px',
    borderBottom: '1px solid var(--color-border, #f3f4f6)',
    cursor: 'pointer',
    color: 'var(--color-text, #1a202c)',
  },
  botItemHover: {
    backgroundColor: '#f9fafb',
  },
  botItemLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  botInfo: {
    display: 'flex',
    flexDirection: 'column',
  },
  botName: {
    fontSize: '14px',
    fontWeight: 500,
    margin: 0,
    color: 'var(--color-text, #1a202c)',
  },
  botMeta: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '12px',
    color: 'var(--color-text-muted, #6b7280)',
  },
  toggleContainer: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    cursor: 'pointer',
  },
  toggleTrack: {
    width: '36px',
    height: '20px',
    backgroundColor: 'var(--color-border)',
    borderRadius: '10px',
    position: 'relative',
  },
  toggleTrackActive: {
    backgroundColor: '#22c55e',
  },
  toggleThumb: {
    position: 'absolute',
    width: '16px',
    height: '16px',
    backgroundColor: 'white',
    borderRadius: '50%',
    top: '2px',
    left: '2px',
    transition: 'transform 0.2s',
    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
  },
  toggleThumbActive: {
    transform: 'translateX(16px)',
  },
  spinner: {
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    border: '2px solid var(--color-border)',
    borderTopColor: '#22c55e',
    animation: 'spin 1s linear infinite',
  },
  footerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px',
    borderTop: '1px solid var(--color-border, #f3f4f6)',
  },
  footerButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    background: 'none',
    border: 'none',
    padding: '0',
    fontSize: '14px',
    cursor: 'pointer',
  },
  createBotButton: {
    color: '#22c55e',
    fontWeight: 500,
  },
  manageBotButton: {
    color: 'var(--color-text-muted, #6b7280)',
    fontWeight: 500,
  },
  creditIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '12px',
    fontWeight: 500,
    cursor: 'pointer',
    borderRadius: '4px',
    padding: '2px 4px',
    transition: 'background-color 0.2s',
  },
  creditSection: {
    padding: '12px',
    borderBottom: '1px solid var(--color-border, #f3f4f6)',
  },
  creditWarningContainer: {
    margin: '8px 0',
  },
};

type BotStatusComponentProps = {
  triggerMode?: 'popover' | 'quickAgentsSidebar';
  /**
   * Dense collapsed chip: status dot + label + chevron only, dropping the credit
   * count and warning icon. Used in the mobile app-bar where the chip must stay
   * compact and one-line; the full credit/warning detail still lives in the
   * expanded popover.
   */
  dense?: boolean;
};

const BotStatusComponent = ({ triggerMode = 'popover', dense = false }: BotStatusComponentProps) => {
  const { t } = useTranslation(namespace);
  const [isExpanded, setIsExpanded] = useState(false);
  const [togglingBots, setTogglingBots] = useState<Set<string>>(new Set());
  const dispatch = useAppDispatch();
  const router = useRouter();
  const instanceId = React.useRef(`bot-status-${Math.random().toString(36).substr(2, 9)}`).current;
  const isDarkMode = useAppSelector(selectIsDarkMode);
  const accessToken = useAppSelector((state) => state.UserReducer.accessToken);
  const { isOpen: isLayoutSidebarOpen, componentToShow } = useAppSelector((state) => state.BtLayoutSidebarReducer);
  
  // Credit management
  const {
    creditStatus,
    isLoading: creditLoading,
    shouldShowWarning: shouldShowCreditWarning,
    warningLevel
  } = useCreditManagement();
  
  // Handle opening credits page for extension context
  const handleOpenCreditsPage = () => {
    if (accessToken) {
      openCreditsPage(accessToken, true);
    }
  };
  
  // Get bots from the Redux store
  const bots: BotType[] = useAppSelector((state) => botSelectByQuery(state.DBBotReducer, {}));
  const isLoading = useAppSelector(state => state.DBBotReducer.itemsLoading);
  
  // Credit indicator component
  const CreditIndicator = ({ compact = false }) => {
    const isLow = creditStatus.isLowCredits || creditStatus.isOutOfCredits;
    const credits = creditStatus.creditsRemaining;
    
    if (compact) {
      // Reads as a quiet secondary figure inside the chip, not as a second chip:
      // no fill, no border — the "low credits" state is what earns colour.
      return (
        <span
          className={`${hs.meta} ${isLow ? hs.metaBad : ''}`}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', cursor: isLow ? 'pointer' : 'default' }}
          onClick={(e) => {
            if (isLow) {
              e.stopPropagation();
              handleOpenCreditsPage();
            }
          }}
          title={isLow ? 'Click to add credits' : `${credits} credits remaining`}
        >
          <Zap size={12} />
          {credits}
        </span>
      );
    }

    // Dark mode colors
    const bgColor = isDarkMode 
      ? (isLow ? 'rgba(239, 68, 68, 0.15)' : 'rgba(34, 197, 94, 0.15)')
      : (isLow ? '#fef2f2' : '#f0fdf4');
    const textColor = isDarkMode
      ? (isLow ? '#fca5a5' : '#86efac')
      : (isLow ? '#dc2626' : '#15803d');
    const borderColor = isDarkMode
      ? (isLow ? 'rgba(239, 68, 68, 0.3)' : 'rgba(34, 197, 94, 0.3)')
      : (isLow ? '#fecaca' : '#bbf7d0');
    const iconColor = isDarkMode
      ? (isLow ? '#fca5a5' : '#86efac')
      : (isLow ? '#ef4444' : '#22c55e');

    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '4px 8px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 500,
        backgroundColor: bgColor,
        color: textColor,
        border: `1px solid ${borderColor}`
      }}>
        <Zap size={12} color={iconColor} />
        <span>{credits} credits</span>
      </div>
    );
  };
  
  // Load bots data when component mounts
  useEffect(() => {
    dispatch(DBBotThunks.find({ $paginate: false }));
    dispatch(DBBotDeploymentThunks.find({ $paginate: false }));
  }, [dispatch]);
  
  // Filter for active bots (those with an activeDeploymentId)
  const activeBots = bots.filter(bot => !!bot.activeDeploymentId);

  
  const toggleBotStatus = async (bot: BotType, event: React.MouseEvent) => {
    // Prevent the click from bubbling to parent and expanding/collapsing
    event.stopPropagation();
    event.preventDefault();

    if (!bot?._id || togglingBots.has(bot._id)) return;
    const botId = bot._id;

    const isPublished = !!bot.activeDeploymentId;

    // Mark bot as toggling
    setTogglingBots(prev => new Set(prev).add(botId));

    try {
      // Toggle bot activation using the same thunk from BotListContent
      await dispatch(toggleBotActivationThunk({
        bot,
        shouldActivate: !isPublished,
        findLatestDeployment: !isPublished
      }));
    } catch (error) {
      console.error('Failed to toggle bot activation:', error);
    } finally {
      // Remove bot from toggling state
      setTogglingBots(prev => {
        const next = new Set(prev);
        next.delete(botId);
        return next;
      });
    }
  };

  const navigateToBotManagement = (event: React.MouseEvent) => {
    // Prevent the click from bubbling to parent
    event.stopPropagation();
    router.navigate('/bot-list-view');
    setIsExpanded(false);
  };

  const createNewBot = async (event: React.MouseEvent) => {
    // Prevent the click from bubbling to parent
    event.stopPropagation();
    setIsExpanded(false);

    // Use the same approach as in BotListContent
    await dispatch(setIsReadyToLoadFlow(false));
    dispatch(createAndReRouteToNewBotEditorThunk({ navigate: router.navigate }));
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (date: Date) => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) {
      return formatTime(date);
    } else if (date.toDateString() === yesterday.toDateString()) {
      return `Yesterday, ${formatTime(date)}`;
    } else {
      return date.toLocaleDateString() + ', ' + formatTime(date);
    }
  };
  
  const getStatusIcon = (status: boolean) => {
    if (status) {
      return <CheckCircle size={16} color="#22c55e" />;
    } else {
      return <Power size={16} color="#9ca3af" />;
    }
  };

  // Get status color based on active bots
  // The chip's single accent. Replaces the hard-coded green/orange/grey dot
  // colours with a header-row tone, so this chip agrees with the plan /
  // keep-awake / engine chips instead of carrying its own palette.
  // Bots that exist but are switched off are a STATE, not a problem — the user
  // turned them off — so they read neutral grey, not the amber warn wash.
  const getStatusTone = () => {
    if (activeBots.length > 0) return hs.toneOk;
    return hs.toneNeutral;
  };

  const navigateToBotDetail = (bot: BotType) => {
    router.navigate(`/bot-detail-view/${bot._id}`);
    setIsExpanded(false);
  };

  // Render the content of the expanded view
  const renderExpandedContent = () => {
    if (isLoading) {
      return (
        <div style={styles.loadingContainer}>
          <div style={styles.loadingSpinner}></div>
          <p style={styles.loadingText}>{t('botStatus.loadingBots')}</p>
        </div>
      );
    }
    
    return (
      <div>
        {/* Credit Section */}
        <div style={styles.creditSection}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            marginBottom: shouldShowCreditWarning ? '8px' : '0'
          }}>
            <CreditIndicator />
            <button 
              onClick={handleOpenCreditsPage}
              style={{
                backgroundColor: shouldShowCreditWarning ? '#dc2626' : '#22c55e',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '4px 8px',
                fontSize: '12px',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => {
                if (!shouldShowCreditWarning) {
                  e.currentTarget.style.backgroundColor = '#16a34a';
                }
              }}
              onMouseLeave={(e) => {
                if (!shouldShowCreditWarning) {
                  e.currentTarget.style.backgroundColor = '#22c55e';
                }
              }}
              data-action-button="true"
            >
              {t('botStatus.addCredits')}
            </button>
          </div>
          
          {shouldShowCreditWarning && (
            <div style={{
              padding: '8px',
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '6px',
              marginTop: '8px'
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                <AlertTriangle size={14} color="#dc2626" style={{ marginTop: '1px', flexShrink: 0 }} />
                <div>
                  <p style={{ 
                    margin: 0, 
                    fontSize: '12px', 
                    fontWeight: 500, 
                    color: '#991b1b',
                    lineHeight: '16px'
                  }}>
                    {creditStatus.isOutOfCredits ? t('botManagement.outOfCredits') : t('botManagement.runningLowOnCredits')}
                  </p>
                  <p style={{ 
                    margin: '2px 0 0 0', 
                    fontSize: '11px', 
                    color: '#dc2626',
                    lineHeight: '14px'
                  }}>
                    {creditStatus.isOutOfCredits 
                      ? t('botManagement.outOfCreditsMessage')
                      : t('botManagement.runningLowMessage')
                    }
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {bots.map(bot => {
          const isActive = !!bot.activeDeploymentId;
          const lastActive = bot.botStats?.latestDeployment?.createdAt 
            ? new Date(bot.botStats.latestDeployment.createdAt) 
            : new Date();
          const isToggling = bot._id ? togglingBots.has(bot._id) : false;
          
          return (
            <div 
              key={bot._id} 
              style={styles.botItem}
              onClick={() => navigateToBotDetail(bot)}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-surface-muted, #f9fafb)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <div style={styles.botItemLeft}>
                {getStatusIcon(isActive)}
                <div style={styles.botInfo}>
                  <p style={styles.botName}>{bot.name || t('botStatus.unnamedBot')}</p>
                  <div style={styles.botMeta}>
                    {bot.botStats?.latestDeployment?.version && (
                      <span style={{ marginRight: '4px' }}>{t('botStatus.version', { version: bot.botStats.latestDeployment.version })}</span>
                    )}
                    {bot.botStats?.latestDeployment?.createdAt && (
                      <>
                        <span style={{ margin: '0 4px' }}>•</span>
                        <Clock size={12} style={{ margin: '0 4px', color: 'var(--color-text-muted, #9ca3af)' }} />
                        <span>{formatDate(lastActive)}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div 
                style={styles.toggleContainer}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  if (!isToggling) {
                    toggleBotStatus(bot, e);
                  }
                }}
                data-action-button="true"
              >
                {isToggling ? (
                  <div style={styles.spinner}></div>
                ) : (
                  <>
                    <input 
                      type="checkbox" 
                      checked={isActive} 
                      onChange={() => {}}
                      readOnly
                      style={{ display: 'none' }}
                    />
                    <div 
                      style={{
                        ...styles.toggleTrack,
                        ...(isActive ? styles.toggleTrackActive : {})
                      }}
                    >
                      <div 
                        style={{
                          ...styles.toggleThumb,
                          ...(isActive ? styles.toggleThumbActive : {})
                        }}
                      ></div>
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        })}
        
        {/* Footer with Create Bot button */}
        <div style={styles.footerContainer}>
          <button 
            onClick={createNewBot}
            style={{...styles.footerButton, ...styles.createBotButton}}
            data-action-button="true"
          >
            <Plus size={16} />
            <span>{t('botManagement.createNewBot')}</span>
          </button>
          
          <button 
            onClick={navigateToBotManagement}
            style={{...styles.footerButton, ...styles.manageBotButton}}
            data-action-button="true"
          >
            <span>{t('botManagement.manageAllBots')}</span>
            <ArrowRight size={16} />
          </button>
        </div>
      </div>
    );
  };

  // Portal container logic - try global portal first, fallback to local
  const localPortalContainer = usePortalContainer();
  const [globalPortalContainer, setGlobalPortalContainer] = useState<HTMLElement | ShadowRoot | null>(null);

  useEffect(() => {
    // Try to find the global portal container (LabelsModals)
    const globalPortal = document.getElementById('labels-modals')?.shadowRoot?.querySelector('.btcss-portal-container');
    if (globalPortal) {
      setGlobalPortalContainer(globalPortal as HTMLElement);
    }
  }, []);

  const targetContainer = globalPortalContainer || localPortalContainer;
  const shouldOpenQuickAgentsSidebar = triggerMode === 'quickAgentsSidebar';
  const isQuickAgentsSidebarOpen = isLayoutSidebarOpen && componentToShow === 'QuickAgents';

  // This chip is for the legacy bots. Accounts without one don't see it at all —
  // the new agents have their own entry points (nav "AI Agents", toolbar button).
  if (bots.length === 0) return null;

  return (
    <div style={styles.container}>
      <Popover
        open={shouldOpenQuickAgentsSidebar ? false : isExpanded}
        onOpenChange={(open) => {
          if (!shouldOpenQuickAgentsSidebar) {
            setIsExpanded(open);
          }
        }}
      >
        <PopoverTrigger asChild>
          <button
            className={`${hs.chip} ${hs.chipButton} ${getStatusTone()}`}
            onClick={(e) => {
              e.stopPropagation();
              if (shouldOpenQuickAgentsSidebar) {
                if (isQuickAgentsSidebarOpen) {
                  dispatch(btLayoutSidebarActions.closeSidebar());
                } else {
                  const maybeOpenWithScope = (btLayoutSidebarActions as any)?.openQuickAgentsWithScope;
                  if (typeof maybeOpenWithScope === 'function') {
                    dispatch(maybeOpenWithScope('all'));
                  } else {
                    dispatch({ type: 'btLayoutSidebar/openWithComponent', payload: 'QuickAgents' });
                  }
                }
                setIsExpanded(false);
              }
            }}
            data-button-id={instanceId}
            data-action-button="true"
          >
            <div className={hs.dot} />
            <span className={hs.label}>
              {activeBots.length > 0
                ? activeBots.length === 1
                  ? `1 ${t('status.botActive')}`
                  : `${activeBots.length} ${t('status.botsActive')}`
                : t('status.botsInactive')}
            </span>
            {!dense && <CreditIndicator compact />}
            {!dense && shouldShowCreditWarning && (
              <AlertTriangle size={13} className={hs.iconBad} />
            )}
            {!shouldOpenQuickAgentsSidebar && (
              isExpanded ? (
                <ChevronDown size={15} className={hs.iconMuted} />
              ) : (
                <ChevronUp size={15} className={hs.iconMuted} />
              )
            )}
          </button>
        </PopoverTrigger>

        {!shouldOpenQuickAgentsSidebar && (
        <PopoverContent
          align="end"
          className="w-auto p-0 border-none shadow-xl rounded-xl"
          container={targetContainer}
          zIndex={2147483647}
          style={{
            direction: 'inherit',
            padding: '16px',
            maxHeight: '80vh',
            overflowY: 'auto',
            minWidth: '380px',
          }}
        >
          <div style={{
            marginBottom: '12px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            color: 'var(--color-text, #1a202c)',
          }}>
            <h3 style={{
              fontWeight: 500,
              margin: 0,
              color: 'var(--color-text, #1a202c)',
            }}>{t('botManagement.title')}</h3>
          </div>
          {renderExpandedContent()}
        </PopoverContent>
        )}
      </Popover>
    </div>
  );
};

export default BotStatusComponent; 
